import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, type LibraryPicture, type PictureLibrary } from "../api.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { usePhone } from "../hooks/useMedia.js";
import { useEditor } from "./store.js";

const SOURCE_NAMES: Record<LibraryPicture["source"], string> = { generated: "made for the library", uploaded: "your own", clip: "made for a clip" };
const GROW_BY = 12;

/**
 * The studio's picture library: every checked picture, by theme, newest first. A tap puts one in the clip at the paused
 * playhead (or the selected words, or a free spot). "Make more" asks for another batch in the same look; Upload adds your own.
 */
export const PictureGallery: React.FC<{ columns?: number }> = ({ columns }) => {
  const phone = usePhone();
  const toast = useToast();
  const clip = useEditor((s) => s.clip);
  const [lib, setLib] = useState<PictureLibrary | null>(null);
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState<string>("all");
  const [busy, setBusy] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const load = () => api.library().then(setLib).catch((e) => toast(e.message));
  useEffect(() => { load(); }, []);
  // a batch being made: the shelf fills as it goes
  useEffect(() => { if (!lib?.growing) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [lib?.growing]);
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (lib?.items ?? []).filter((i) => i.kind === "image" && (theme === "all" || i.theme === theme) && (!needle || [i.subject, i.theme, ...i.tags, i.prompt].some((t) => t.toLowerCase().includes(needle)))).sort((a, b) => b.addedAt.localeCompare(a.addedAt));
  }, [lib, q, theme]);
  const cols = columns ?? (phone ? 2 : 3);
  const use = async (p: LibraryPicture) => {
    if (!clip || busy) return;
    setBusy(p.id);
    try {
      const st = useEditor.getState();
      await st.flushSave();
      const at = st.newOverlayRange();
      const r = await api.addLibraryOverlay(clip.videoId, clip.id, p.id, at ? { startMs: at.startMs, endMs: at.endMs } : undefined);
      await st.reloadFromServer();
      st.setSelectedOverlay(r.overlay.id);
      toast(`"${p.subject}" is in ${at?.how === "selection" ? "over the selected words" : at?.how === "playhead" ? "at the playhead" : "in a free spot"}`);
      load();
    } catch (e: any) { toast(e.message); } finally { setBusy(null); }
  };
  const grow = async () => { try { await api.growLibrary(GROW_BY); toast(`Making ${GROW_BY} more pictures… they appear here as they are checked`); setLib((l) => (l ? { ...l, growing: true } : l)); } catch (e: any) { toast(e.message); } };
  const upload = (f: File) => api.uploadLibraryImage(f).then((r) => { setLib(r); toast(`"${r.item.subject}" added to the library`); }).catch((e) => toast(e.message));
  const remove = async (p: LibraryPicture) => { if (!confirm(`Remove "${p.subject}" from the library? Clips that use it keep their copy.`)) return; try { setLib(await api.deleteLibraryImage(p.id)); } catch (e: any) { toast(e.message); } };
  return (
    <div className="gallery">
      <div className="gallery-head">
        <div className="gallery-search"><Icon name="search" size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pictures… mosque, sea, lantern" aria-label="Search pictures" /></div>
        <div className="gallery-cats" role="tablist" aria-label="Picture themes">
          <button role="tab" aria-selected={theme === "all"} className={theme === "all" ? "on" : ""} onClick={() => setTheme("all")}>All</button>
          {(lib?.themes ?? []).filter((t) => lib?.items.some((i) => i.theme === t.id)).map((t) => <button key={t.id} role="tab" aria-selected={theme === t.id} className={theme === t.id ? "on" : ""} onClick={() => setTheme(t.id)}>{t.name}</button>)}
        </div>
        <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button className="btn sm gold" onClick={grow} disabled={!!lib?.growing} title={`The AI makes ${GROW_BY} more pictures in the library's look`}><Icon name="star" size={14} />{lib?.growing ? "Making more…" : "Make more"}</button>
          <button className="btn sm" onClick={() => file.current?.click()} title="Add a picture of your own (it is checked first)"><Icon name="plus" size={14} />Upload</button>
          <input ref={file} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
          {lib ? <span className="hint">{lib.items.length} picture{lib.items.length === 1 ? "" : "s"}</span> : null}
        </div>
      </div>
      <div className="gallery-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {items.map((p) => (
          <div key={p.id} className={`gtile pictile${busy === p.id ? " on" : ""}`} role="button" tabIndex={0} data-library={p.id} onClick={() => use(p)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); use(p); } }} title={p.prompt || p.subject}>
            <div style={{ position: "relative", aspectRatio: "9 / 16", background: "var(--panel)" }}>
              <img src={p.url} alt={p.subject} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              {!phone ? <button className="chip" style={{ position: "absolute", top: 6, right: 6 }} aria-label={`Remove ${p.subject}`} onClick={(e) => { e.stopPropagation(); remove(p); }}><Icon name="x" size={11} /></button> : null}
            </div>
            <span className="gtile-name">{p.subject}{p.uses ? <b>{p.uses} use{p.uses === 1 ? "" : "s"}</b> : null}</span>
            <span className="gtile-tags">{SOURCE_NAMES[p.source]}{p.tags.length ? ` · ${p.tags.slice(0, 3).join(", ")}` : ""}</span>
          </div>
        ))}
        {lib && !items.length ? <div className="hint" style={{ gridColumn: "1 / -1", padding: 12 }}>{lib.items.length ? "No picture matches." : "The library is empty. Press Make more to fill it with pictures in the studio's look, or upload your own."}</div> : null}
      </div>
    </div>
  );
};
